import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  LinearProgress,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Reddit as RedditIcon } from '@mui/icons-material';
import { useAdminContext } from '../AdminContext';

export default function SettingsTab() {
  const {
    canonicalSubredditName,
    user,
    redditLimits,
    subscriptions,
    handleUnsubscribe,
    selectedParentSub,
    setSelectedParentSub,
    availableSubs,
    isLoadingSubscriptions,
    handleSubscribe,
    isSubscribed,
    hasAuth,
    handleInitiateRedditAuth,
    isAuthingReddit,
    handlePushWikiPages,
    isPushingWiki,
    handleFixWikiPages,
    isFixingWikiPages,
    wikiPushStatus,
    wikiFixStatus,
    subreddit,
    handleDiscordAuth,
    isConnectingDiscord,
    discordConnected,
    handleDownloadOldSheet,
    handleUpdateDefaultFlair,
    isUpdatingDefaultFlair,
    updateDefaultFlairProgress,
    handleImportModerators,
    isImportingModerators,
    handleSyncSheets,
    isSyncingSheets,
    handleRebuildMasterSheetFromBaseTables,
    isSyncingMasterSheet,
    setClearDataDialogOpen,
    isClearingData,
    subredditSettings,
    handleUpdateSubredditSettings,
    isLoadingSubredditSettings,
    isUpdatingSubredditSettings,
  } = useAdminContext();

  const [logoStem, setLogoStem] = useState('');
  const [wikiPages, setWikiPages] = useState<string[]>([]);
  const [wikiPage, setWikiPage] = useState('');
  const [isLoadingWikiPages, setIsLoadingWikiPages] = useState(false);

  useEffect(() => {
    const v = subredditSettings?.default_logo_image_source_override;
    setLogoStem(v != null ? String(v) : '');
  }, [subredditSettings?.default_logo_image_source_override]);

  useEffect(() => {
    if (!subreddit || !user) return;
    let cancelled = false;
    (async () => {
      setIsLoadingWikiPages(true);
      try {
        const { API_BASE_URL } = await import('../../../config');
        const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/wiki-pages`, {
          headers: { Authorization: `Bearer ${user.username}` },
        });
        const data = await response.json();
        if (!cancelled && response.ok && Array.isArray(data.pages)) {
          setWikiPages(data.pages);
        }
      } catch {
        /* typing a page name still works */
      } finally {
        if (!cancelled) setIsLoadingWikiPages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subreddit, user]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Subreddit Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Manage settings for /r/{canonicalSubredditName}</Typography>

      {isSubscribed && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Homepage logo</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional image stem for this sub’s tile on the flair wizard home (basename without <code>.png</code>). The
            file must exist on the CDN under this sub’s folder, same as flair template assets.
          </Typography>
          <TextField
            size="small"
            label="image_source stem"
            value={logoStem}
            onChange={(e) => setLogoStem(e.target.value)}
            fullWidth
            sx={{ maxWidth: 420, mb: 2 }}
            disabled={isLoadingSubredditSettings || isUpdatingSubredditSettings || !user}
          />
          <Button
            variant="contained"
            onClick={() => handleUpdateSubredditSettings('default_logo_image_source_override', logoStem.trim() || null)}
            disabled={isUpdatingSubredditSettings || !user}
          >
            Save logo
          </Button>
        </Box>
      )}

      {redditLimits && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Reddit Limits</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Stylesheet: {Math.round(redditLimits.stylesheet.total_size / 1024)} KB / {Math.round(redditLimits.stylesheet.limit / 1024)} KB</Typography>
                <Typography variant="body2" color={redditLimits.stylesheet.total_usage_percent > 90 ? 'error' : redditLimits.stylesheet.total_usage_percent > 75 ? 'warning.main' : 'text.secondary'}>{redditLimits.stylesheet.total_usage_percent.toFixed(1)}%</Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 8, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(redditLimits.stylesheet.flair_usage_percent || 0, 100)}%`, backgroundColor: '#ff9800', borderRadius: '4px 0 0 4px' }} />
                <Box sx={{ position: 'absolute', left: `${Math.min(redditLimits.stylesheet.flair_usage_percent || 0, 100)}%`, top: 0, height: '100%', width: `${Math.min(redditLimits.stylesheet.non_flair_usage_percent || 0, 100)}%`, backgroundColor: '#2196f3', borderRadius: redditLimits.stylesheet.flair_usage_percent === 0 ? '4px' : '0 4px 4px 0' }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#ff9800' }}>Flair CSS: {Math.round((redditLimits.stylesheet.flair_css_size || 0) / 1024)} KB ({redditLimits.stylesheet.flair_usage_percent.toFixed(1)}%)</Typography>
                <Typography variant="caption" sx={{ color: '#2196f3' }}>Other: {Math.round((redditLimits.stylesheet.non_flair_css_size || 0) / 1024)} KB ({redditLimits.stylesheet.non_flair_usage_percent?.toFixed(1) || '0.0'}%)</Typography>
              </Box>
              {redditLimits.stylesheet.total_usage_percent > 75 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Getting close to limit. Reduce CSS or optimize stylesheet.</Typography>
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Emojis: {redditLimits.emojis.current_count} / {redditLimits.emojis.limit}</Typography>
                <Typography variant="body2" color={redditLimits.emojis.usage_percent > 90 ? 'error' : redditLimits.emojis.usage_percent > 75 ? 'warning.main' : 'text.secondary'}>{redditLimits.emojis.usage_percent.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(redditLimits.emojis.usage_percent, 100)} color={redditLimits.emojis.usage_percent > 90 ? 'error' : redditLimits.emojis.usage_percent > 75 ? 'warning' : 'primary'} sx={{ height: 8, borderRadius: 1 }} />
              {redditLimits.emojis.usage_percent > 75 && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Getting close to limit.</Typography>}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Default Flairs: {redditLimits.default_flairs.current_count} / {redditLimits.default_flairs.limit}</Typography>
                <Typography variant="body2" color={redditLimits.default_flairs.usage_percent > 90 ? 'error' : redditLimits.default_flairs.usage_percent > 75 ? 'warning.main' : 'text.secondary'}>{redditLimits.default_flairs.usage_percent.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(redditLimits.default_flairs.usage_percent, 100)} color={redditLimits.default_flairs.usage_percent > 90 ? 'error' : redditLimits.default_flairs.usage_percent > 75 ? 'warning' : 'primary'} sx={{ height: 8, borderRadius: 1 }} />
              {redditLimits.default_flairs.usage_percent > 75 && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Getting close to limit.</Typography>}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Images: {redditLimits.images.total_images_registered} / {redditLimits.images.limit}</Typography>
                <Typography variant="body2" color={redditLimits.images.usage_percent && redditLimits.images.usage_percent > 90 ? 'error' : redditLimits.images.usage_percent && redditLimits.images.usage_percent > 75 ? 'warning.main' : 'text.secondary'}>{redditLimits.images.usage_percent ? redditLimits.images.usage_percent.toFixed(1) : '0'}%</Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 8, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(redditLimits.images.spritesheet_usage_percent || 0, 100)}%`, backgroundColor: '#ff9800', borderRadius: '4px 0 0 4px' }} />
                <Box sx={{ position: 'absolute', left: `${Math.min(redditLimits.images.spritesheet_usage_percent || 0, 100)}%`, top: 0, height: '100%', width: `${Math.min(redditLimits.images.non_spritesheet_usage_percent || 0, 100)}%`, backgroundColor: '#2196f3', borderRadius: redditLimits.images.spritesheet_usage_percent === 0 ? '4px' : '0 4px 4px 0' }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#ff9800' }}>Spritesheets: {redditLimits.images.spritesheet_images_count ?? 0}</Typography>
                <Typography variant="caption" sx={{ color: '#2196f3' }}>Other: {redditLimits.images.non_spritesheet_images_count ?? 0}</Typography>
              </Box>
              {redditLimits.images.usage_percent && redditLimits.images.usage_percent > 75 && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Getting close to limit.</Typography>}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Each image must be ≤512 KB.</Typography>
              {redditLimits.images.over_limit && Array.isArray(redditLimits.images.over_limit) && redditLimits.images.over_limit.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>{redditLimits.images.over_limit.length} image(s) exceed 512 KB limit.</Alert>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {isSubscribed && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Reddit templates &amp; sub metadata</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update default flair templates and rescrape moderator info for <strong>/r/{canonicalSubredditName}</strong> on Reddit.
            Default templates use the parent sub&apos;s master sheet when this sub is subscribed to another sub&apos;s flair.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateDefaultFlair}
                disabled={isUpdatingDefaultFlair || !user}
                title="Update Reddit flair templates for this subreddit"
              >
                {isUpdatingDefaultFlair ? 'Updating…' : 'Update Flair Templates'}
              </Button>
              {isUpdatingDefaultFlair && (
                <Box sx={{ width: '100%', maxWidth: 420 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {(() => {
                      const pr = updateDefaultFlairProgress?.progress as
                        | { phase?: string; created?: number; total_to_create?: number }
                        | undefined;
                      if (!pr?.phase) return 'Connecting…';
                      if (pr.phase === 'starting') return 'Starting…';
                      if (pr.phase === 'deleting') return 'Deleting existing Reddit flair templates…';
                      if (pr.phase === 'creating') {
                        return `Creating templates: ${pr.created ?? 0} / ${pr.total_to_create ?? '…'}`;
                      }
                      if (pr.phase === 'done') return 'Finishing…';
                      return '';
                    })()}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleImportModerators}
              disabled={isImportingModerators || !user}
            >
              {isImportingModerators ? 'Rescraping...' : 'Rescrape Sub Info'}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Reddit Authentication</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Authenticate with Reddit to enable spritesheet and emoji uploads.</Typography>
        {hasAuth === false && <Alert severity="warning" sx={{ mb: 2 }}>No Reddit authorization found. Please authenticate.</Alert>}
        <Button variant="contained" color="primary" startIcon={<RedditIcon />} onClick={handleInitiateRedditAuth} disabled={isAuthingReddit || !user} sx={{ mb: 2 }}>
          {isAuthingReddit ? 'Redirecting...' : 'Auth Reddit Flair'}
        </Button>
        {!user && <Alert severity="warning" sx={{ mt: 1 }}>Please log in first to authenticate with Reddit.</Alert>}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Push to Reddit</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Push wiki pages (flair, inlineflair) and stylesheet updates to Reddit.</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="contained" color="primary" startIcon={<RedditIcon />} onClick={handlePushWikiPages} disabled={isPushingWiki || isFixingWikiPages || !user}>{isPushingWiki ? 'Pushing...' : 'Push to Reddit'}</Button>
          <Autocomplete
            freeSolo
            options={wikiPages}
            value={wikiPage}
            onInputChange={(_e, value) => setWikiPage(value)}
            loading={isLoadingWikiPages}
            sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Wiki page"
                placeholder="All pages"
                helperText="Leave blank to scan every wiki page"
              />
            )}
          />
          <Button variant="outlined" color="primary" onClick={() => handleFixWikiPages(wikiPage)} disabled={isPushingWiki || isFixingWikiPages || !user}>{isFixingWikiPages ? 'Fixing...' : 'Fix Wiki Pages'}</Button>
        </Box>
        {wikiPushStatus && (
          <Box sx={{ mt: 2 }}>
            {wikiPushStatus.flair && <Alert severity={wikiPushStatus.flair.success ? 'success' : 'error'} sx={{ mb: 1 }}>wiki/flair: {wikiPushStatus.flair.success ? '✓ Success' : `✗ ${wikiPushStatus.flair.error}`}</Alert>}
            {wikiPushStatus.inlineflair && <Alert severity={wikiPushStatus.inlineflair.success ? 'success' : 'error'} sx={{ mb: 1 }}>wiki/inlineflair: {wikiPushStatus.inlineflair.success ? '✓ Success' : `✗ ${wikiPushStatus.inlineflair.error}`}</Alert>}
            {wikiPushStatus.stylesheet && <Alert severity={wikiPushStatus.stylesheet.success ? 'success' : 'error'}>about/stylesheet: {wikiPushStatus.stylesheet.success ? '✓ Success' : `✗ ${wikiPushStatus.stylesheet.error}`}</Alert>}
          </Box>
        )}
        {wikiFixStatus && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={wikiFixStatus.errors && wikiFixStatus.errors.length > 0 ? 'warning' : 'success'}>
              {wikiFixStatus.message || `Checked ${wikiFixStatus.pages_checked || 0} page(s), fixed ${wikiFixStatus.pages_fixed || 0} reference(s).`}
              {wikiFixStatus.permission_warnings && wikiFixStatus.permission_warnings.length > 0 && <Box sx={{ mt: 1, fontSize: '0.875rem' }}><strong>Permission warnings:</strong> {wikiFixStatus.permission_warnings.length} page(s).</Box>}
              {wikiFixStatus.errors && wikiFixStatus.errors.length > 0 && <Box sx={{ mt: 1 }}><strong>Errors:</strong> {wikiFixStatus.errors.join(', ')}</Box>}
            </Alert>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Discord Integration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Connect Discord for moderator roles and subreddit channels.</Typography>
        <Button variant="contained" color="secondary" onClick={handleDiscordAuth} disabled={!user || isConnectingDiscord} sx={{ mb: 2 }}>
          {isConnectingDiscord ? 'Connecting...' : 'Connect Discord Account'}
        </Button>
        {discordConnected && <Alert severity="success" sx={{ mt: 1 }}>Discord account connected!</Alert>}
      </Box>

      {!isSubscribed && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Export & Import</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="primary" onClick={handleDownloadOldSheet} disabled={!user}>Download Old Sheet</Button>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Button variant="contained" color="primary" onClick={handleUpdateDefaultFlair} disabled={isUpdatingDefaultFlair || !user} title="Update Reddit flair templates">{isUpdatingDefaultFlair ? 'Updating…' : 'Update Flair Templates'}</Button>
              {isUpdatingDefaultFlair && (
                <Box sx={{ width: '100%', maxWidth: 420 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {(() => {
                      const pr = updateDefaultFlairProgress?.progress as
                        | { phase?: string; created?: number; total_to_create?: number }
                        | undefined;
                      if (!pr?.phase) return 'Connecting…';
                      if (pr.phase === 'starting') return 'Starting…';
                      if (pr.phase === 'deleting') return 'Deleting existing Reddit flair templates…';
                      if (pr.phase === 'creating') {
                        return `Creating templates: ${pr.created ?? 0} / ${pr.total_to_create ?? '…'}`;
                      }
                      if (pr.phase === 'done') return 'Finishing…';
                      return '';
                    })()}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button variant="contained" color="primary" onClick={handleImportModerators} disabled={isImportingModerators || !user}>{isImportingModerators ? 'Rescraping...' : 'Rescrape Sub Info'}</Button>
            <Tooltip title="Uses master_sheet as source: populates base tables (class/family/genus/flair/image) from master_sheet, then syncs sheet/selectable. Use when master_sheet is correct.">
              <span><Button variant="contained" color="warning" onClick={handleSyncSheets} disabled={isSyncingSheets || isSyncingMasterSheet || !user}>{isSyncingSheets ? 'Syncing...' : 'Sync Sheets'}</Button></span>
            </Tooltip>
            <Tooltip title="Full rebuild: deletes all master_sheet rows, repopulates from base tables (image/flair/genus/family/class). Use when master_sheet is out of sync and base tables are correct.">
              <span><Button variant="contained" color="info" onClick={handleRebuildMasterSheetFromBaseTables} disabled={isSyncingSheets || isSyncingMasterSheet || !user}>{isSyncingMasterSheet ? 'Rebuilding...' : 'Rebuild Master Sheet'}</Button></span>
            </Tooltip>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (
                  !window.confirm(
                    `Are you sure? This opens the flow to permanently delete ALL flair sheet data for /r/${canonicalSubredditName} (classes through master_sheet). You can still cancel on the next screen.`
                  )
                ) {
                  return;
                }
                setClearDataDialogOpen(true);
              }}
              disabled={isClearingData || !user}
            >
              {isClearingData ? 'Clearing...' : 'Clear Flair Sheet'}
            </Button>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, letterSpacing: 0.4 }}>
        One-time / restrictive choices (confirm before each action)
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>Subscribe to Sub Flair</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Subscribe to another subreddit to receive all flair updates. When subscribed, this subreddit is read-only for flair.
        </Typography>
        {subscriptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Current Subscriptions:</Typography>
            {subscriptions.map((parentSub: string) => (
              <Chip key={parentSub} label={`/r/${parentSub}`} onDelete={() => handleUnsubscribe(parentSub)} sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel shrink sx={{ backgroundColor: 'background.paper', px: 1 }}>Select Subreddit</InputLabel>
            <Select value={selectedParentSub} onChange={(e) => setSelectedParentSub(e.target.value)} native disabled={isLoadingSubscriptions}>
              <option value="">-- Select Subreddit --</option>
              {availableSubs.map((sub: string) => (
                <option key={sub} value={sub}>/r/{sub}</option>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedParentSub) return;
              if (
                !window.confirm(
                  `Subscribe /r/${canonicalSubredditName} to /r/${selectedParentSub} for flair data?\n\nFlair management on this sub becomes read-only and is intended as a long-term setup. Continue?`
                )
              ) {
                return;
              }
              handleSubscribe();
            }}
            disabled={!selectedParentSub || isLoadingSubscriptions}
          >
            Subscribe
          </Button>
        </Box>
        {isSubscribed && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This subreddit is subscribed. Flair management is read-only. Changes sync nightly.
          </Alert>
        )}
      </Box>

      {!isSubscribed && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Flair mode</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Simplified flair is for subreddits where users pick a single image via emoji and the rest of the flair text is free-form (no CSS class templates). Only one sheet of images is needed; Flair Wizard will not store or enforce Reddit flair CSS.
          </Typography>
          {isLoadingSubredditSettings && <LinearProgress sx={{ mb: 1 }} />}
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={!!subredditSettings?.simplified_flair}
                  onChange={(_, v) => {
                    if (isLoadingSubredditSettings || isUpdatingSubredditSettings || !user) return;
                    const current = !!subredditSettings?.simplified_flair;
                    if (v === current) return;
                    const msg = v
                      ? 'Turn ON simplified flair? Users use emoji-only templates; Reddit flair CSS classes are not enforced. This is a major mode change.'
                      : 'Turn OFF simplified flair? This sub will use standard CSS class flairs again.';
                    if (!window.confirm(`${msg}\n\nAre you sure?`)) return;
                    handleUpdateSubredditSettings('simplified_flair', v);
                  }}
                  disabled={isLoadingSubredditSettings || isUpdatingSubredditSettings || !user}
                />
              }
              label="Simplified flair"
            />
          </FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', maxWidth: 720 }}>
            When enabled: syncing user flairs saves <code>flair1</code> (and optional <code>flair2</code>) from <code>:emoji:</code> codes in flair text only; <code>flair_css</code> is left empty. Nightly validation and the “validate user flairs” tool skip CSS/text enforcement so you can still count how many users use each emoji.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
